import { Fragment } from "react";
import EditIcon from "@mui/icons-material/Edit";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  getStockCountItemDisplayName,
  STOCK_COUNT_ADJUSTMENT_INDICATOR_COLORS,
  STOCK_COUNT_ITEM_TYPE,
  STOCK_COUNT_ITEM_TYPE_LABELS,
} from "../utils/stockCountItemModel";
import {
  formatSignedStockCountQuantity,
  formatStockCountQuantity,
} from "../utils/stockCountItemFormatters";

const columns = [
  { key: "indicator", width: "12px", label: "" },
  { key: "item", label: "Producto", width: "minmax(180px, 1.6fr)" },
  { key: "counter", label: "Contador", width: "minmax(88px, 0.7fr)" },
  { key: "auditor", label: "Auditor", width: "minmax(88px, 0.7fr)" },
  { key: "difference", label: "Diferencia", width: "minmax(92px, 0.75fr)" },
  {
    key: "theoretical",
    label: "Stock Teórico",
    width: "minmax(100px, 0.85fr)",
  },
  { key: "adjustment", label: "Ajuste", width: "minmax(92px, 0.75fr)" },
  { key: "countUnit", label: "Unidad de Conteo", width: "minmax(110px, 0.85fr)" },
  { key: "actions", label: "Acciones", width: "minmax(72px, 0.55fr)" },
];

const gridSx = {
  display: "grid",
  gridTemplateColumns: columns.map((column) => column.width).join(" "),
  columnGap: 0.75,
  alignItems: "center",
  "& > *": { minWidth: 0 },
};

function TextCell({ children, align = "center", title }) {
  return (
    <Typography
      noWrap
      title={title ?? (typeof children === "string" ? children : undefined)}
      sx={{
        color: "primary.900",
        fontSize: 13,
        fontWeight: 650,
        textAlign: align,
      }}
    >
      {children}
    </Typography>
  );
}

function AdjustmentIndicator({ adjustmentType }) {
  const color =
    STOCK_COUNT_ADJUSTMENT_INDICATOR_COLORS[adjustmentType] ||
    STOCK_COUNT_ADJUSTMENT_INDICATOR_COLORS.DEFAULT;

  return (
    <Box
      aria-hidden
      sx={{
        width: 4,
        height: 34,
        borderRadius: "6px",
        bgcolor: color,
        justifySelf: "center",
      }}
    />
  );
}

function GroupSeparator({ itemType }) {
  const label = STOCK_COUNT_ITEM_TYPE_LABELS[itemType];
  if (!label) return null;

  return (
    <Box
      role="heading"
      aria-level={3}
      data-testid={`stock-count-group-${itemType}`}
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 2.25,
        py: 0.65,
        bgcolor: "common.white",
        color: "primary.900",
      }}
    >
      <Box aria-hidden sx={{ width: 62, borderTop: "1px solid", opacity: 0.7 }} />
      <Typography
        component="span"
        sx={{ fontSize: 14, fontWeight: 800, lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Box aria-hidden sx={{ flex: 1, borderTop: "1px solid", opacity: 0.7 }} />
    </Box>
  );
}

export default function StockCountItemsTable({
  rows = [],
  loading = false,
  readOnly = false,
  editingLineId = null,
  onEdit,
  emptyMessage = "No hay productos contados para mostrar.",
}) {
  const renderedGroups = new Set();

  return (
    <Box sx={{ flex: 1, minHeight: 0, borderRadius: "20px", overflow: "hidden" }}>
      <Box sx={{ height: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <Box
          sx={{
            minWidth: 980,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              ...gridSx,
              bgcolor: "neutral.600",
              flexShrink: 0,
              px: 1.5,
              py: 1.35,
            }}
          >
            {columns.map((column) => {
              const header = (
                <Typography
                  key={column.key}
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "logoblanco",
                    lineHeight: 1.15,
                    textAlign: column.key === "item" ? "left" : "center",
                  }}
                >
                  {column.label}
                </Typography>
              );

              if (column.key !== "difference") return header;

              return (
                <Tooltip
                  key={column.key}
                  title="La diferencia es la cantidad contada menos el stock teórico"
                  arrow
                >
                  {header}
                </Tooltip>
              );
            })}
          </Box>

          <Box
            sx={{
              bgcolor: "common.white",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            {rows.map((item) => {
              const itemType =
                item.itemType === STOCK_COUNT_ITEM_TYPE.PREPARED_PRODUCT
                  ? STOCK_COUNT_ITEM_TYPE.PREPARED_PRODUCT
                  : STOCK_COUNT_ITEM_TYPE.PURCHASE_ITEM;
              const showGroupSeparator = !renderedGroups.has(itemType);
              renderedGroups.add(itemType);
              const itemName = getStockCountItemDisplayName(item.itemSnapshot);
              const canEdit = !readOnly && Boolean(onEdit);
              const isEditing = editingLineId === item.id;
              const hasStockConflict = Boolean(item.hasStockConflict);

              return (
                <Fragment key={item.id}>
                  {showGroupSeparator ? (
                    <GroupSeparator itemType={itemType} />
                  ) : null}
                  <Box
                    data-testid={`stock-count-item-row-${item.id}`}
                    data-item-type={itemType}
                    data-stock-conflict={hasStockConflict ? "true" : undefined}
                    sx={{
                      px: 1.5,
                      py: 0.95,
                      borderBottom: "1px solid rgba(0,0,0,.05)",
                      borderLeft: hasStockConflict
                        ? "4px solid"
                        : "4px solid transparent",
                      borderLeftColor: hasStockConflict
                        ? "warning.main"
                        : "transparent",
                      minHeight: 52,
                      bgcolor: hasStockConflict
                        ? "rgba(237, 108, 2, 0.08)"
                        : isEditing
                          ? "rgba(47, 128, 237, 0.04)"
                          : "common.white",
                      "&:hover": {
                        bgcolor: hasStockConflict
                          ? "rgba(237, 108, 2, 0.12)"
                          : isEditing
                            ? "rgba(47, 128, 237, 0.06)"
                            : "rgba(0,0,0,.015)",
                      },
                    }}
                  >
                    <Box sx={gridSx}>
                      <AdjustmentIndicator adjustmentType={item.adjustmentType} />
                      <TextCell align="left" title={itemName}>
                        {itemName}
                      </TextCell>
                      <TextCell>
                        {formatStockCountQuantity(item.counterQuantity, {
                          maxFractionDigits: 2,
                        })}
                      </TextCell>
                      <TextCell>
                        {formatStockCountQuantity(item.auditorQuantity, {
                          maxFractionDigits: 2,
                        })}
                      </TextCell>
                      <TextCell>
                        {formatSignedStockCountQuantity(item.differenceQuantity, {
                          maxFractionDigits: 2,
                        })}
                      </TextCell>
                      <TextCell>
                        {formatStockCountQuantity(
                          item.visibleTheoreticalQuantity,
                          { maxFractionDigits: 2 }
                        )}
                      </TextCell>
                      <TextCell>
                        {formatSignedStockCountQuantity(
                          item.finalAdjustmentQuantity,
                          { maxFractionDigits: 2 }
                        )}
                      </TextCell>
                      <TextCell>{item.countUnitSnapshot || "—"}</TextCell>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ justifyContent: "center", alignItems: "center" }}
                      >
                        {hasStockConflict ? (
                          <Tooltip
                            title="Este ajuste dejaría el stock por debajo de cero."
                            arrow
                          >
                            <WarningAmberOutlinedIcon
                              sx={{ fontSize: 17, color: "warning.main" }}
                            />
                          </Tooltip>
                        ) : null}
                        {canEdit ? (
                          <Tooltip title="Editar ajuste" arrow>
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Editar ajuste de ${itemName}`}
                                disabled={loading}
                                onClick={() => onEdit?.(item)}
                                sx={{
                                  width: 31,
                                  height: 31,
                                  bgcolor: "neutral.50",
                                  boxShadow: "0px 2px 8px rgba(0,0,0,.08)",
                                  "&:hover": { bgcolor: "secondary.100" },
                                }}
                              >
                                <EditIcon
                                  sx={{ fontSize: 17, color: "primary.700" }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </Box>
                  </Box>
                </Fragment>
              );
            })}

            {rows.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography sx={{ color: "neutral.700", fontWeight: 650 }}>
                  {loading ? "Cargando productos del conteo..." : emptyMessage}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
